import { lazy, Suspense, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import HeroSection from "@/components/sections/HeroSection";
import AboutSection from "@/components/sections/AboutSection";
import AIEnginesSection from "@/components/sections/AIEnginesSection";
import DesignStudio from "@/components/sections/DesignStudio.jsx";
import FeaturedProjectsSection from "@/components/sections/FeaturedProjectsSection";
import PlatformShowcaseSection from "@/components/sections/PlatformShowcaseSection.jsx";
import VideoSection from "@/components/sections/VideoSection";
import ApplicationsSection from "@/components/sections/ApplicationsSection";
import TrustSection from "@/components/sections/TrustSection";
import ContactSection from "@/components/sections/ContactSection";
import AuthCallback from "@/components/auth/AuthCallback";
import { ProtectedRoute, InvestorRoute } from "@/components/auth/AuthProvider";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const SolutionsPage = lazy(() => import("@/pages/SolutionsPage"));
const ARExperiencePage = lazy(() => import("@/pages/ARExperiencePage"));
const CompaniesPage = lazy(() => import("@/pages/CompaniesPage"));
const TrainingAcademyPage = lazy(() => import("@/pages/TrainingAcademyPage"));
const MobileScannerPage = lazy(() => import("@/pages/MobileScannerPage"));
const MobileLoginPage = lazy(() => import("@/pages/MobileLoginPage"));
const InvestorPresentationPage = lazy(() => import("@/pages/InvestorPresentationPage"));
const InvestorLandingPage = lazy(() => import("@/pages/InvestorLandingPage"));
const AcquirePage = lazy(() => import("@/pages/AcquirePage"));
const RndDashboardPage = lazy(() => import("@/pages/RndDashboardPage"));
const PatentMappingPage = lazy(() => import("@/pages/PatentMappingPage"));
const ProjectDetailPage = lazy(() => import("@/pages/ProjectDetailPage"));
const ProjectsGalleryPage = lazy(() => import("@/pages/ProjectsGalleryPage"));
const PublicProjectDetailPage = lazy(() => import("@/pages/PublicProjectDetailPage"));
const MobileAuthDebugPage = lazy(() => import("@/pages/MobileAuthDebugPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const SecurityPolicyPage = lazy(() => import("@/pages/SecurityPolicyPage"));
const TrustCenterPage = lazy(() => import("@/pages/TrustCenterPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const MarketplacePage = lazy(() => import("@/pages/MarketplacePage"));
const LicensingPage = lazy(() => import("@/pages/LicensingPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const BecomeSellerPage = lazy(() => import("@/pages/BecomeSellerPage"));
const EngineComposePage = lazy(() => import("@/pages/EngineComposePage"));
const EnginesDeepDivePage = lazy(() => import("@/pages/EnginesDeepDivePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const NexusAssistantPage = lazy(() => import("@/pages/NexusAssistantPage"));
import PWAInstallPrompt from "@/components/pwa/PWAInstallPrompt";
import EmailPopupModal from "@/components/marketing/EmailPopupModal";
import StickyCTA from "@/components/marketing/StickyCTA";
import CookieConsent from "@/components/gdpr/CookieConsent";
import { Toaster } from "@/components/ui/sonner";
import { SeoManager } from "@/seo/SeoManager";
import GlobalNexusVoiceAssistant from "@/components/GlobalNexusVoiceAssistant";

// Home page with all sections - Streamlined for investor presentation
function HomePage() {
  return (
    <div className="App min-h-screen bg-[#060710] overflow-x-hidden">
      <Header />
      <main>
        <HeroSection />
        <VideoSection />
        <AboutSection />
        <PlatformShowcaseSection />
        <AIEnginesSection />
        <DesignStudio />
        <FeaturedProjectsSection />
        <ApplicationsSection />
        <TrustSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

// App Router - handles session_id detection before routing
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (synchronously during render)
  // CRITICAL: This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<div role="status" className="min-h-screen bg-[#060710] text-white p-8">Loading…</div>}>
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/studio" element={<Navigate to="/#studio" replace />} />
      {/* Backward-compat redirects — old marketing links */}
      <Route path="/gallery" element={<Navigate to="/projects" replace />} />
      <Route path="/applications" element={<Navigate to="/#applications" replace />} />
      <Route path="/projects" element={<ProjectsGalleryPage />} />
      <Route path="/projects/:projectId" element={<PublicProjectDetailPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route 
        path="/ar" 
        element={
          <ProtectedRoute>
            <ARExperiencePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/companies" 
        element={
          <ProtectedRoute>
            <CompaniesPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/training" element={<TrainingAcademyPage />} />
      <Route path="/scanner" element={<MobileScannerPage />} />
      <Route path="/mobile-login" element={<MobileLoginPage />} />
      <Route
        path="/investors" 
        element={
          <InvestorRoute>
            <InvestorPresentationPage />
          </InvestorRoute>
        } 
      />
      <Route path="/investor" element={<InvestorLandingPage />} />
      <Route path="/acquire" element={<AcquirePage />} />
      <Route path="/rnd-dashboard" element={<RndDashboardPage />} />
      <Route path="/patent-mapping" element={<PatentMappingPage />} />
      <Route path="/mobile-debug" element={<MobileAuthDebugPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/security-policy" element={<SecurityPolicyPage />} />
      <Route path="/security" element={<TrustCenterPage />} />
      <Route path="/trust" element={<TrustCenterPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/accessibility" element={<AccessibilityPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/licensing" element={<LicensingPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/become-seller" element={<BecomeSellerPage />} />
      <Route path="/compose" element={<EngineComposePage />} />
      <Route path="/engines" element={<EnginesDeepDivePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/nexus" element={<NexusAssistantPage />} />
      <Route 
        path="/project/:projectId" 
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        } 
      />
      {/* 404 - Must be last */}
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Load the 3D viewer only after a model enters the page.
  useEffect(() => {
    if (typeof customElements === 'undefined' || customElements.get('model-viewer')) return;
    let started = false;
    const loadIfNeeded = () => {
      if (started || !document.querySelector('model-viewer')) return;
      started = true;
      observer.disconnect();
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
      script.onerror = () => { started = false; };
      document.head.appendChild(script);
    };
    const observer = new MutationObserver(loadIfNeeded);
    observer.observe(document.body, { childList: true, subtree: true });
    loadIfNeeded();
    return () => observer.disconnect();
  }, []);

  return (
    <LanguageProvider>
      <Router>
        <SeoManager />
        <AppRouter />
        <GlobalNexusVoiceAssistant />
        <PWAInstallPrompt />
        <StickyCTA />
        <CookieConsent />
        <Toaster position="bottom-right" richColors />
      </Router>
    </LanguageProvider>
  );
}

export default App;
